import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useChatSidebarStore } from "../../stores/chatSidebarStore";
import { getPagePhaseForPath, getDefaultPathForPhase } from "./pagePhaseMap";
import { useJourneyPhase } from "./useCoachOrchestrator";
import { JOURNEY_STATES } from "./types";
import AiCoachChat from "./AiCoachChat";

// Global, portal-rendered coach chat sidebar. Mounted once in Layout.
// Desktop: docked panel on the right, below the header, toggled by a tab
// hugging the right edge. Mobile: full-height overlay, toggled the same way.
export default function ChatSidebar() {
  const isOpen = useChatSidebarStore((s) => s.isOpen);
  const mode = useChatSidebarStore((s) => s.mode);
  const pageMatchActive = useChatSidebarStore((s) => s.pageMatchActive);
  const dismissedForPath = useChatSidebarStore((s) => s.dismissedForPath);
  const closeChat = useChatSidebarStore((s) => s.closeChat);
  const toggle = useChatSidebarStore((s) => s.toggle);
  const applyPageMatch = useChatSidebarStore((s) => s.applyPageMatch);
  const dismissPageMatch = useChatSidebarStore((s) => s.dismissPageMatch);
  const revertToDefault = useChatSidebarStore((s) => s.revertToDefault);
  const clearPageMatch = useChatSidebarStore((s) => s.clearPageMatch);
  const location = useLocation();
  const navigate = useNavigate();
  // The automatic, non-overridden phase the state machine currently
  // recommends, and the page that goes with it.
  const defaultPhase = useJourneyPhase();
  const defaultPath = getDefaultPathForPhase(defaultPhase);
  const defaultPhaseLabel = defaultPhase?.kind
    ? (JOURNEY_STATES.find((s) => s.kind === defaultPhase.kind)?.label ??
      defaultPhase.kind.replace(/_/g, " "))
    : null;

  // Journey Phase Nudging: while the sidebar is open, on pages that map to a
  // phase, keep the active phase aligned with whatever page the user is on,
  // re-checking on every navigation. Skipped for the pathname the user just
  // explicitly backed out of via the banner, until they navigate elsewhere.
  // Navigating to a page with no phase mapping while a page-match override
  // is still active reverts the phase back to default — the override only
  // makes sense while the user is actually on the page it came from.
  useEffect(() => {
    if (!isOpen) return;
    const pagePhase = getPagePhaseForPath(location.pathname);
    if (!pagePhase) {
      if (pageMatchActive) clearPageMatch();
      return;
    }
    if (location.pathname === dismissedForPath) return;
    if (mode === pagePhase && pageMatchActive) return;
    applyPageMatch(pagePhase);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, location.pathname]);

  // Mismatch banner — INTENTIONALLY not gated on `pageMatchActive` / the
  // auto-override effect above. This is a general "you're not on the page
  // your journey currently recommends" nudge, not an "undo the override
  // that just happened" affordance, so it must show even when the current
  // page has no `pagePhaseMap` entry at all (no override ever fired here).
  // Condition is purely: we know a default-phase page to send the user to,
  // that page isn't the one they're on, and they haven't already dismissed
  // it for this exact path. Computed regardless of `isOpen` so the collapsed
  // tab can surface it too (see `needsAttention` below) — it's only rendered
  // as the in-panel banner while `isOpen`.
  // Do NOT add a `pageMatchActive` (or similar "override is active") check
  // here — that would hide the banner on unmapped pages, which is wrong.
  const mismatchActive =
    !!defaultPath &&
    defaultPath !== location.pathname &&
    location.pathname !== dismissedForPath;
  const showMismatchBanner = isOpen && mismatchActive;
  // Collapsed-tab CTA: same condition, surfaced while closed so the tab
  // itself signals there's something to look at before the user opens it.
  const needsAttention = !isOpen && mismatchActive;

  const handleDismissPageMatch = () => dismissPageMatch(location.pathname);
  // Selecting the banner: go to the one thing that matters — wherever the
  // current (default, state-machine-recommended) journey phase lives — and
  // drop any page-driven override back to that default.
  const handleRevertToDefault = () => {
    revertToDefault(location.pathname);
    if (defaultPath && defaultPath !== location.pathname) navigate(defaultPath);
  };

  return createPortal(
    <>
      {/* Toggle tab - hugs the right edge. Hidden on mobile once open (the
          full-height overlay has its own close affordance); stays visible on
          desktop, shifted left by the panel width. When `needsAttention` is
          true (mismatch banner would show if opened), becomes a CTA: amber
          to match the banner it exposes, with a label and a pulse ring
          instead of the plain "Coach" treatment. */}
      <button
        type="button"
        onClick={toggle}
        aria-label={
          isOpen
            ? "Close coach chat"
            : needsAttention
              ? "Open coach chat — journey update available"
              : "Open coach chat"
        }
        className={`fixed top-1/2 -translate-y-1/2 z-50 text-xs font-semibold px-1.5 py-3 rounded-l-md shadow-md writing-mode-vertical transition-[right] ${
          needsAttention
            ? "bg-indigo-600 text-white ring-2 ring-indigo-400 animate-pulse"
            : "bg-indigo-600 text-white"
        } ${isOpen ? "hidden md:block md:right-[360px]" : "right-0"}`}
        style={{ writingMode: "vertical-rl" }}
      >
        {needsAttention ? "Coach •" : "Coach"}
      </button>

      {/* Mobile: full-height overlay */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-transform duration-200 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="w-full h-full bg-bg-primary border-l border-border shadow-xl">
          <AiCoachChat
            key={mode ?? "auto"}
            variant="panel"
            journeyPhaseOverrideKind={mode}
            isExpandedOverride
            onCollapse={closeChat}
            pageMatchActive={showMismatchBanner}
            defaultPhaseLabel={defaultPhaseLabel}
            onDismissPageMatch={handleDismissPageMatch}
            onRevertToDefault={handleRevertToDefault}
          />
        </div>
      </div>

      {/* Desktop: docked panel, below the header (nav h-16 => top-16) */}
      <div
        className={`hidden md:block fixed top-16 bottom-0 right-0 z-40 w-[360px] bg-bg-primary border-l border-border shadow-xl transition-transform duration-200 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <AiCoachChat
          key={mode ?? "auto"}
          variant="panel"
          journeyPhaseOverrideKind={mode}
          isExpandedOverride
          onCollapse={closeChat}
          pageMatchActive={showMismatchBanner}
          defaultPhaseLabel={defaultPhaseLabel}
          onDismissPageMatch={handleDismissPageMatch}
          onRevertToDefault={handleRevertToDefault}
        />
      </div>
    </>,
    document.body,
  );
}
